import React, { useState } from "react";
import { useFormik } from "formik";
import { VStack, Button, Text, Box } from "@chakra-ui/react";
import PasswordInputField from "@/components/PasswordInputField";
import { IChangePasswordData } from "@/types/common";
import * as Yup from "yup";
import { CHANGE_PASSWORD } from "@/api/mutations";
import { useMutation } from "@apollo/client";

const validationSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .min(8, "Old password must be at least 8 characters")
    .required("Old password is required"),

  newPassword1: Yup.string()
    .min(8, "New password must be at least 8 characters")
    .required("New password is required")
    .oneOf([Yup.ref("newPassword2")], "Passwords not  match"),

  newPassword2: Yup.string()
    .min(8, "New password must be at least 8 characters")
    .oneOf([Yup.ref("newPassword1")], "Passwords not  match")
    .required("Confirm password is required"),
});
export const ChangePasswordForm = () => {
  const [error, setError] = useState<string | null>(null);

  const [changePassword] = useMutation(CHANGE_PASSWORD);

  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword1: false,
    newPassword2: false,
  });

  const toggleVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const { handleSubmit, values, handleChange, errors, touched } =
    useFormik<IChangePasswordData>({
      initialValues: {
        oldPassword: "",
        newPassword1: "",
        newPassword2: "",
      },
      onSubmit: async (formData) => {
        try {
          const response = await changePassword({
            variables: {
              oldPassword: formData.oldPassword,
              newPassword1: formData.newPassword1,
              newPassword2: formData.newPassword2,
            },
          });

          const result = response.data.changePassword;

          if (result.__typename === "ErrorType") {
            setError(result.message);
          }
        } catch (err) {
          setError("Unexpected error. Please try again later.");
        }
      },

      validationSchema,
    });

  return (
    <form onSubmit={handleSubmit} name="change-password-form">
      <Text fontWeight="500" mb={4} fontSize={16}>
        Change password:
      </Text>
      <VStack align="end" gap={4} width={480}>
        <PasswordInputField
          label="Old password"
          name="oldPassword"
          value={values.oldPassword}
          error={errors.oldPassword}
          touched={touched.oldPassword}
          show={showPasswords.oldPassword}
          onToggle={() => toggleVisibility("oldPassword")}
          onChange={handleChange}
        />

        <PasswordInputField
          label="New password"
          name="newPassword1"
          value={values.newPassword1}
          error={errors.newPassword1}
          touched={touched.newPassword1}
          show={showPasswords.newPassword1}
          onToggle={() => toggleVisibility("newPassword1")}
          onChange={handleChange}
        />

        <PasswordInputField
          label="Confirm password"
          name="newPassword2"
          value={values.newPassword2}
          error={errors.newPassword2}
          touched={touched.newPassword2}
          show={showPasswords.newPassword2}
          onToggle={() => toggleVisibility("newPassword2")}
          onChange={handleChange}
        />
        <Box
          display={"flex"}
          width={"100%"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {" "}
          <Box width="100%" textAlign="left">
            {error && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {error}
              </Text>
            )}
          </Box>
          <Button
            width={76}
            height={45}
            borderRadius={5}
            bg="brand.focusRing"
            color="brand.contrast"
            type="submit"
          >
            Save
          </Button>
        </Box>
      </VStack>
    </form>
  );
};
